import { useEffect, useRef, useState } from 'react';
import Location from './Location/Location';
import Draw from './Draw/Draw';
import Distance from './Distance/Distance';
import Wait from './Wait/Wait';
import Results from './Results/Results';
import Start from './Start';
import './CreateRoute.css';
import './PageDefaults.css';

const DEFAULT_LAT = 39.1419;
const DEFAULT_LON = -84.4991;
const DEFAULT_ALLOW_ROTATION = true;
const DEFAULT_STEP_SIZE = 20;
const DEFAULT_NUM_RESULTS = 5;

const CreateRoute = () => {
  const [stage, setStage] = useState('start');
  const [paramState] = useState(new ParamState());

  const [locationReady, setLocationReady] = useState(false);
  const [locationBlobId, setLocationBlobId] = useState(null);

  const [lat, setLat] = useState(DEFAULT_LAT);
  const [lon, setLon] = useState(DEFAULT_LON);
  const [locationResolved, setLocationResolved] = useState(false);
  const userSetLocationRef = useRef(false);

  const resolveLocation = () => {
    if (!navigator.geolocation) {
      setLocationResolved(true);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        if (userSetLocationRef.current) return;
        setLat(position.coords.latitude);
        setLon(position.coords.longitude);
        setLocationResolved(true);
      },
      () => {
        // Keep default location if user location isn't available.
        setLocationResolved(true);
      },
      { enableHighAccuracy: true, timeout: 5000 }
    );
  };

  useEffect(() => {
    resolveLocation();
  }, []);

  const handleLocationChange = (nextLat, nextLon) => {
    userSetLocationRef.current = true;
    setLat(nextLat);
    setLon(nextLon);
  };
  const loadLocation = (lat, lon, areaSize) => {
    paramState.lat = lat;
    paramState.lon = lon;
    paramState.areaSize = areaSize;

    fetch(`${process.env.REACT_APP_BACKEND_URL}/loadLocation`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ lat, lon, areaSize })
    })
      .then(res => res.json())
      .then(data => {
        setLocationBlobId(data.blobId);
        setLocationReady(true);
      })
      .catch(err => console.error("Error:", err));

    setStage('draw');
  };

  const [estimate, setEstimate] = useState(0);
  const getDistanceEstimate = async (drawing, isLoop) => {
    paramState.drawing = drawing;
    paramState.isLoop = isLoop;

    setStage('distance');

    const estimatedDist = await estimateDistance(drawing);
    setEstimate(estimatedDist);
  }

  async function estimateDistance(drawing) {
    const blob = await new Promise(resolve => drawing.toBlob(resolve, "image/png"));
    const formData = new FormData();
    formData.append("file", blob, "drawing.png");
    formData.append("scale", 10);

    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/project-distance`, {
        method: "POST",
        body: formData,
      });

      if (!res.ok) {
        throw new Error(`Server error ${res.status}`);
      }

      const data = await res.json();
      return data.estimated_distance_meters.toFixed(2);
    } catch (err) {
      console.error("Error estimating distance", err);
    }
  }

  const setDistanceMult = (mult) => {
    paramState.distanceMult = mult;
    setParams(DEFAULT_ALLOW_ROTATION, DEFAULT_STEP_SIZE, DEFAULT_NUM_RESULTS);
  }

  const setParams = (allowRotation, stepSize, numResults) => {
    paramState.allowRotation = allowRotation;
    paramState.stepSize = stepSize;
    paramState.numResults = numResults;

    getResultsAsync();

    setStage('wait');
  }

  const locationReadyRef = useRef(locationReady);
  const locationBlobIdRef = useRef(locationBlobId);
  useEffect(() => {
    locationReadyRef.current = locationReady;
    locationBlobIdRef.current = locationBlobId;
  }, [locationReady, locationBlobId]);
  const waitForLocationReady = () => {
    return new Promise((resolve) => {
      if (locationReadyRef.current) {
        resolve(locationBlobIdRef.current);
      } else {
        const interval = setInterval(() => {
          if (locationReadyRef.current) {
            clearInterval(interval);
            resolve(locationBlobIdRef.current);
          }
        }, 100);
      }
    });
  };

  const [waitState, setWaitState] = useState('Loading Map...');
  const [waitStateI, setWaitStateI] = useState(1);
  const [results, setResults] = useState([]);
  const activeSocketRef = useRef(null);
  const socketCompletedRef = useRef(false);

  const resetParamState = () => {
    paramState.lat = undefined;
    paramState.lon = undefined;
    paramState.areaSize = undefined;
    paramState.drawing = undefined;
    paramState.isLoop = undefined;
    paramState.distanceMult = undefined;
    paramState.allowRotation = undefined;
    paramState.stepSize = undefined;
    paramState.numResults = undefined;
    paramState.blobId = undefined;
  };

  const resetAll = () => {
    if (activeSocketRef.current && activeSocketRef.current.readyState !== WebSocket.CLOSED) {
      activeSocketRef.current.close(1000, 'Reset');
    }
    activeSocketRef.current = null;
    socketCompletedRef.current = false;
    setStage('start');
    setLocationReady(false);
    setLocationBlobId(null);
    setEstimate(0);
    setWaitState('Loading Map...');
    setWaitStateI(1);
    setResults([]);
    setLat(DEFAULT_LAT);
    setLon(DEFAULT_LON);
    setLocationResolved(false);
    userSetLocationRef.current = false;
    resetParamState();
    resolveLocation();
  };

  const handleProgressUpdate = (data) => {
    if (data.phase === 'done') {
      setWaitState('Done!');
      setWaitStateI(5);

      setTimeout(() => {
        setStage('results');
        setResults(data.results);
      }, 2000);
    } else {
      setWaitState(data.phase);
      setWaitStateI(data.progress);
    }
  }

  const getResultsAsync = async () => {
    paramState.blobId = await waitForLocationReady();
    if (activeSocketRef.current && activeSocketRef.current.readyState !== WebSocket.CLOSED) {
      activeSocketRef.current.close(1000, 'New request');
    }
    activeSocketRef.current = null;
    socketCompletedRef.current = false;
    openRouteWebSocket(handleProgressUpdate);
  }

  function openRouteWebSocket(onMessage) {
    return new Promise(async (resolve, reject) => {
      const ws = new WebSocket(`${process.env.REACT_APP_BACKEND_BASE_URL}/ws/findRoutes`);
      activeSocketRef.current = ws;

      const imageBase64 = await new Promise((res) => {
        paramState.drawing.toBlob((blob) => {
          if (!blob) return res(null);
          const reader = new FileReader();
          reader.onloadend = () => {
            res(reader.result.split(",")[1]);
          };
          reader.readAsDataURL(blob);
        }, "image/png");
      });

      if (!imageBase64) {
        setWaitState('Failed to read drawing.');
        setWaitStateI(0);
        return reject(new Error("Failed to read canvas image"));
      }

      ws.onopen = () => {
        console.log("WebSocket connected");

        ws.send(JSON.stringify({
          ...paramState,
          drawing: imageBase64
        }));

        resolve(ws);
      };

      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        if (data.phase === 'done') {
          socketCompletedRef.current = true;
          ws.close(1000, 'Completed');
        }
        onMessage(data);
      };

      ws.onerror = (err) => {
        console.error("WebSocket error", err);
        setWaitState('Connection error. Please try again.');
        setWaitStateI(0);
        reject(err);
      };

      ws.onclose = () => {
        console.log("WebSocket closed");
        if (!socketCompletedRef.current) {
          setWaitState('Connection lost. Please try again.');
          setWaitStateI(0);
        }
      };
    });
  }

  return (
    <div className='background'>
      <div className='container'>
        {stage === 'start' ? (
          <Start beginRoute={() => setStage('location')}></Start>
        ) : stage === 'location' ? (
          <Location
            loadLocation={loadLocation}
            lat={lat}
            lon={lon}
            onLocationChange={handleLocationChange}
            locationResolved={locationResolved}
          ></Location>
        ) : stage === 'draw' ? (
          <Draw getDistanceEstimate={getDistanceEstimate}></Draw>
        ) : stage === 'distance' ? (
          <Distance setDistanceMult={setDistanceMult} estimatedDistance={estimate}></Distance>
        ) : stage === 'wait' ? (
          <Wait progressMessage={waitState} progressStage={waitStateI}></Wait>
        ) : stage === 'results' ? (
          <Results
            changeParams={() => setStage('distance')}
            newDrawing={() => setStage('draw')}
            startOver={resetAll}
            results={results}
          ></Results>
        ) : ""}
      </div>
    </div>
  )
}

export default CreateRoute;

class ParamState {
  lat;
  lon;
  areaSize;
  drawing;
  isLoop;
  distanceMult;
  allowRotation;
  stepSize;
  numResults;
  blobId;
}